import { useEffect, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Container,
  LinearProgress,
  Button,
  Link,
} from "@mui/material";
import { useNavigate, useLocation, Navigate } from "react-router-dom";

import { useNav } from "../../../context/NavProvider";
import { useAnalytics } from "../../../context/AnalyticsProvider";
import { ANALYTICS_TAGS } from "../../../constants/analyticsTags";
import { useAccount } from "../../../context/AccountProvider";
import { useClient } from "../../../context/ClientProvider";
import { useArrangement } from "../../../context/ArrangementProvider";
import { useParty } from "../../../context/PartyProvider";

import {disposableIncome as calcDisposable, parentMonthlyTotal} from "../domain/entities/calculators";
import { HomeStatusCode } from "../domain/entities/home";
import { EmploymentStatusCode } from "../domain/entities/employment";

import generateFinancialStatement from "../../../utils/generateFinancialStatement";

import ScriptLoader from "../../../components/account/ScriptLoader";
import Change from "../components/Change";
import Negative from "../components/Negative";
import SummaryTotals from "../components/SummaryTotals";
import ErrorPanel from "../components/ErrorPanel";

import { useFinancialStatementQuery } from "../queries/useSlimFinancialQuery";
import { useSaveFinancialMutation } from "../mutations/useSaveFinancialMutation";
import { sfsExpiredMessage, sfsUpToDateMessage } from "../shared/utils";
import { FinancialStatementView } from "../api/dto/FinancialStatement";
import { buildFixedOther } from "../domain/entities/mapping";

/**
 * Standard financial statement page
 * */
function FinancialStatement() {
  const { setShowFooter } = useNav();
  const { useGetParty } = useParty();
  const { partyId } = useGetParty();
  const { setTag } = useAnalytics();

  const navigate = useNavigate();
  const location = useLocation();

  const { useGetAccount } = useAccount();
  const { clientLogo, useGetConfig } = useClient();
  const { useGetArrangement, loadPlanData, planLoaded } = useArrangement();

  const { clientConfig } = useGetConfig();
  const { account, isAccountLoading } = useGetAccount();

  const { arrangement, isArrangementLoading } = useGetArrangement();

  const financialQuery = useFinancialStatementQuery(partyId);

  const saveFinancial = useSaveFinancialMutation(partyId)



  useEffect(() => {
    if (!isAccountLoading && !isArrangementLoading && !planLoaded) {
      loadPlanData(arrangement);
    }
  }, [planLoaded, isArrangementLoading, isAccountLoading, arrangement, loadPlanData]);

  useEffect(() => {
    if (account && planLoaded && financialQuery.data) {
      setTag(ANALYTICS_TAGS.SFS_STARTED, { path: location.pathname });
    }
  }, [account, planLoaded, financialQuery.data, setTag, location.pathname]);

  const disposableIncome = useMemo(() => {
    const totals = financialQuery.data?.totals ?? null;
    return calcDisposable(totals);
  }, [financialQuery.data?.totals]);

  if (isAccountLoading || financialQuery.isLoading || isArrangementLoading) {
    return <LinearProgress />;
  }

  if (!financialQuery.data || financialQuery.data?.mode?.mode === "edit" || financialQuery.data?.mode?.mode === "new") {
    return <Navigate to="/sfs/about?mode=edit" replace />;
  }

    const handleGeneratePdf = async () => {
        await generateFinancialStatement({
            sfsIncome: financialQuery.data?.income,
            sfsExpenditureFlexible: financialQuery.data?.flexible,
            sfsAdditionalDebt: financialQuery.data?.debts,
            sfsExpenditureFixed: financialQuery.data?.fixed,
            sfsFixedOther: parentMonthlyTotal(buildFixedOther(financialQuery.data?.fixedOther ?? [])),
            sfsSavings: financialQuery.data?.monthlySavingsAmount ?? 0,
            sfsTotals: financialQuery.data?.totals,
            clientLogo, disposableIncome,
        });
    };

  const handleComplete = async () => {
    try {
      await saveFinancial.mutateAsync(financialQuery.data as Omit<FinancialStatementView, "totals">);
    } catch (e: any) {
      throw new Error(e);
    } finally {
      navigate("/repayment");
    }
  };

  return (
    account &&
    planLoaded &&
    financialQuery.data &&
    disposableIncome !== null &&
    disposableIncome !== undefined && (
      <>
        <ScriptLoader />
        <Container sx={{ mt: 4, mb: 7 }}>
          {(financialQuery.isError && financialQuery.error?.message) ||
            (saveFinancial.isError && saveFinancial.error?.message && (
              <ErrorPanel
                error={
                  financialQuery.error?.message ?? saveFinancial.error?.message
                }
              />
            ))}
          {/* SFS complete but out of date */}
          {financialQuery.data?.mode?.mode === "review" &&
            financialQuery.data?.mode?.monthsSinceReview >= 6 &&
            disposableIncome > 0 && (
              <Box>
                <Typography fontSize={24} fontWeight={"bold"}>
                  Before you set up a repayment plan
                </Typography>
                <Typography sx={{ pt: 3, pb: 5 }}>
                  {sfsExpiredMessage}
                </Typography>

                <Stack
                  spacing={{ md: 2 }}
                  direction={{
                    xs: "column",
                    md: "row",
                  }}
                >
                  {/* Summary */}
                  <Box sx={{ width: { xs: "100%", md: "60%" } }}>
                    <SummaryTotals
                      title="Summary"
                      disposableIncome={disposableIncome}
                      stepTotals={financialQuery.data?.totals?.totals ?? []}
                    />
                  </Box>

                  {/* Links to change */}
                  <Box sx={{ width: { xs: "100%", md: "40%" } }}>
                    <Change
                      totals={financialQuery.data?.totals?.totals}
                      employmentStatus={
                        financialQuery.data
                          ?.customersEmploymentStatus as keyof EmploymentStatusCode
                      }
                      homeStatus={
                        financialQuery.data.homeStatus as keyof HomeStatusCode
                      }
                    />
                  </Box>
                </Stack>

                <Typography fontSize={24} fontWeight={"bold"}>
                  My figures are up to date
                </Typography>
                <Typography sx={{ pt: 3, pb: 5 }}>
                  {sfsUpToDateMessage}
                </Typography>
                <Button
                  id="save-continue-btn"
                  onClick={handleComplete}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    borderRadius: 0,
                    width: { xs: "20em", lg: "15em" },
                    backgroundColor: "primary.dark",
                    "&:hover": {
                      backgroundColor: "primary.light",
                      color: "primary.dark",
                    },
                  }}
                >
                  Save & Continue
                </Button>
              </Box>
            )}

          {/* SFS complete and in date */}
          {((financialQuery.data?.mode?.mode === "review" &&
            financialQuery.data?.mode?.monthsSinceReview < 6) ||
            (financialQuery.data?.mode?.mode === "review" &&
              financialQuery.data?.mode?.monthsSinceReview >= 6 &&
              disposableIncome <= 0)) && (
            <Box>
              {disposableIncome > 0 && (
                <Box>
                  <Typography fontSize={24} fontWeight={"bold"}>
                    Income & Expenditure
                  </Typography>
                  <Typography sx={{ pt: 1 }}>
                    Thanks for completing your Financial Statement
                  </Typography>
                  <Typography sx={{ pt: 1, pb: 5 }}>
                    We appreciate that submitting details about your money can
                    feel daunting, but this is a really positive way to check-in
                    on your financial health.
                  </Typography>
                </Box>
              )}
              {disposableIncome <= 0 && (
                <Box>
                  <Typography fontSize={24} fontWeight={"bold"}>
                    About your financial statement
                  </Typography>
                  <Typography sx={{ pt: 1 }}>
                    Based on the current details submitted in your financial
                    statement, it appears that you are unable to set up a plan
                    online. Please contact us on{" "}
                    <Link
                      href={`tel:${clientConfig?.layout?.arrearsCard?.tel}`}
                    >
                      {clientConfig?.layout?.arrearsCard?.tel}
                    </Link>{" "}
                    (or{" "}
                    <Link
                      href={`tel:${clientConfig?.layout?.arrearsCard?.telFromAbroad}`}
                    >
                      {clientConfig?.layout?.arrearsCard?.telFromAbroad}
                    </Link>{" "}
                    if you're abroad) and we will be able to assist you
                    further.{" "}
                  </Typography>
                  <Typography sx={{ pt: 1 }}>
                    If your circumstances have changed since you last updated
                    your Financial Statement, you can easily make changes below.
                    Just let us know your current financial situation, and we'll
                    review your eligibility for a payment plan accordingly.{" "}
                  </Typography>
                  <Typography sx={{ pt: 1, pb: 5 }}>
                    If all appears ok you will be able to create/change your
                    repayment plan.
                  </Typography>
                </Box>
              )}
              <Button
                onClick={handleGeneratePdf}
                variant="contained"
                sx={{
                  mb: 8,
                  textTransform: "none",
                  borderRadius: 0,
                  width: { xs: "15em", lg: "20em" },
                  backgroundColor: "primary.dark",
                  color: "primary.contrastText",
                  "&:hover": {
                    backgroundColor: "primary.light",
                    color: "primary.dark",
                  },
                }}
              >
                Download Statement
              </Button>
              <Stack
                spacing={{ md: 2 }}
                direction={{
                  xs: "column",
                  md: "row",
                }}
              >
                <Box sx={{ width: { xs: "100%", md: "60%" } }}>
                  <SummaryTotals
                    title="Summary"
                    stepTotals={financialQuery.data?.totals?.totals}
                    disposableIncome={disposableIncome}
                  />
                </Box>
                <Box sx={{ width: { xs: "100%", md: "40%" } }}>
                  <Change
                    totals={financialQuery.data?.totals?.totals}
                    employmentStatus={
                      financialQuery.data
                        ?.customersEmploymentStatus as keyof EmploymentStatusCode
                    }
                    homeStatus={
                      financialQuery.data?.homeStatus as keyof HomeStatusCode
                    }
                  />
                </Box>
              </Stack>
              {disposableIncome <= 0 && <Negative />}
            </Box>
          )}
        </Container>
            {setShowFooter(true)}
      </>
    )
  );
}

export default FinancialStatement;
